import { useRegisterMfaListener } from '@privy-io/react-auth';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { useWalletMfaVerificationController } from './use-wallet-mfa-verification-controller';
import { WalletMfaVerificationPhase } from './wallet-mfa-types';
import type { WalletMfaVerificationProps } from './wallet-mfa-verification.types';

type WalletSecurityOperation = <T>(_operation: () => Promise<T>) => Promise<T>;

type WalletMfaVerificationContextValue = {
  runSecurityOperation: WalletSecurityOperation;
  verification: WalletMfaVerificationProps;
  registerInlineHost: () => () => void;
};

const WalletMfaVerificationContext = createContext<WalletMfaVerificationContextValue | null>(null);

export const useWalletSecurityOperation = (): WalletSecurityOperation => {
  const context = useContext(WalletMfaVerificationContext);

  if (!context) {
    throw new Error('Wallet MFA verification provider is unavailable.');
  }

  return context.runSecurityOperation;
};

export const useInlineWalletMfaVerification = (
  enabled: boolean,
): WalletMfaVerificationProps | null => {
  const context = useContext(WalletMfaVerificationContext);
  const registerInlineHost = context?.registerInlineHost;

  useEffect(() => {
    if (!enabled || !registerInlineHost) {
      return;
    }

    return registerInlineHost();
  }, [enabled, registerInlineHost]);

  return enabled && context ? context.verification : null;
};

const useInlineHostRegistry = (verificationOpen: boolean, cancelVerification: () => void) => {
  const inlineHostCountRef = useRef(0);
  const verificationOpenRef = useRef(false);
  const cancelVerificationRef = useRef(cancelVerification);
  verificationOpenRef.current = verificationOpen;
  cancelVerificationRef.current = cancelVerification;

  const registerInlineHost = useCallback(() => {
    inlineHostCountRef.current += 1;

    return () => {
      inlineHostCountRef.current = Math.max(0, inlineHostCountRef.current - 1);
      if (inlineHostCountRef.current === 0 && verificationOpenRef.current) {
        cancelVerificationRef.current();
      }
    };
  }, []);

  const hasInlineHost = useCallback(() => inlineHostCountRef.current > 0, []);

  return { registerInlineHost, hasInlineHost };
};

export const WalletMfaVerificationProvider = ({ children }: { children: ReactNode }) => {
  const controller = useWalletMfaVerificationController();
  const {
    state,
    runSecurityOperation,
    handleMfaRequired,
    changeCode,
    verifyCode,
    restart,
    cancelVerification,
  } = controller;
  const { registerInlineHost, hasInlineHost } = useInlineHostRegistry(
    state.phase !== WalletMfaVerificationPhase.Closed,
    cancelVerification,
  );

  useRegisterMfaListener(
    useMemo(
      () => ({ onMfaRequired: (request) => handleMfaRequired(request, hasInlineHost()) }),
      [handleMfaRequired, hasInlineHost],
    ),
  );

  const verification = useMemo<WalletMfaVerificationProps>(
    () => ({
      open: state.phase !== WalletMfaVerificationPhase.Closed,
      phase: state.phase,
      code: state.code,
      error: state.error,
      purpose: state.purpose,
      onCodeChange: changeCode,
      onVerifyCode: verifyCode,
      onRestart: restart,
      onCancel: cancelVerification,
    }),
    [
      cancelVerification,
      changeCode,
      restart,
      state.code,
      state.error,
      state.phase,
      state.purpose,
      verifyCode,
    ],
  );
  const context = useMemo(
    () => ({ runSecurityOperation, verification, registerInlineHost }),
    [registerInlineHost, verification, runSecurityOperation],
  );

  return (
    <WalletMfaVerificationContext.Provider value={context}>
      {children}
    </WalletMfaVerificationContext.Provider>
  );
};
